import { reduce } from 'lodash';

import { __ } from '../i18n/l10n';
import type { BetBuilderItemStatus, BetBuilderSelectionStatus, BetBuilderStatus } from '../types/betbuilder';

import type { BetState } from './entities/bets';
import type { OutcomeState } from './entities/outcomes';

export type BetBuilderItem = {
  matchId: number;
  betId: number;
  oddId?: number;
  outcomeId: number | null;
  outcomeIds: Array<number> | null;
  status: BetBuilderItemStatus;
  error: string | null;
  isFromMultichoice?: boolean;
};

type Selection = {
  items: Array<BetBuilderItem>;
  value: number | null;
  status: BetBuilderSelectionStatus;
  error: string | null;
  ecode: number | null;
};

type BetType = {
  betIds: Array<number>;
};

export type BetBuilderState = {
  selection: Selection;
  betTypesList: Array<number>;
  betTypes: Record<number, BetType>;
  status: BetBuilderStatus;
  error: string | null;
  ecode: number | null;
};

export type BetBuilderRootState = {
  matches: Record<number, BetBuilderState>;
};

type ItemsOutcomesAndBetStoresOptions = {
  isDefinedCheckMode: boolean; // if activated, we check if the value is defined. Nullish values are considered as defined
};

// eslint-disable-next-line sonarjs/prefer-object-literal
class BetBuilder {
  static isMultichoiceItem = (item: BetBuilderItem): boolean => item.outcomeIds !== undefined;

  static addBetTitlePrefix = (betTitle: string): string => __('%1$s (multiple chance)', betTitle);

  /**
   * Check if we have correct saved outcomes and bets on the basket
   *
   * @param {Array<BetBuilderItem>} items the current saved basket items
   * @param {BetState} bets the current basket saved bets
   * @param {OutcomeState} outcomes the current basket saved outcomes
   * @param {BasketOutcomesAndBetStoresOptions} [options] add of defined check mode to determine defined values
   *
   * @return {boolean} if we have correct saved outcomes and bets on the basket
   */
  static checkItemsOutcomesAndBetsState = (
    items: Array<BetBuilderItem>,
    bets: { [key: number]: BetState },
    outcomes: { [key: number]: OutcomeState },
    options?: ItemsOutcomesAndBetStoresOptions,
  ) => {
    const isDefinedCheckMode = options?.isDefinedCheckMode;

    return items.reduce((result, item) => {
      const isBetStored = isDefinedCheckMode ? bets[item.betId] !== undefined : !!bets[item.betId];

      let isOutcomesStored = false;

      if (BetBuilder.isMultichoiceItem(item) && Array.isArray(item.outcomeIds)) {
        isOutcomesStored = item.outcomeIds.reduce((acc, cur) => {
          return acc ? (isDefinedCheckMode ? outcomes[cur] !== undefined : !!outcomes[cur]) : acc;
        }, true);
      } else if (item.outcomeId) {
        isOutcomesStored = isDefinedCheckMode ? outcomes[item.outcomeId] !== undefined : !!outcomes[item.outcomeId];
      }
      return result && isBetStored && isOutcomesStored;
    }, true);
  };

  /**
   * Check if all the basket outcomes and bets are stored.
   *
   * @param {Array<BetBuilderItem>} items the current saved basket items
   * @param {BetState} bets the current basket saved bets
   * @param {OutcomeState} outcomes the current basket saved outcomes
   * @return {boolean} check if we have stored outcomes and bets on the basket
   */
  static isItemsOutcomesAndBetsStored = (
    items: Array<BetBuilderItem>,
    bets: { [key: number]: BetState },
    outcomes: { [key: number]: OutcomeState },
  ) =>
    BetBuilder.checkItemsOutcomesAndBetsState(items, bets, outcomes, {
      isDefinedCheckMode: true,
    });

  /**
   * Check if all the basket outcomes and bets are stored.
   *
   * @param {Array<BetBuilderItem>} items the current saved basket items
   * @param {BetState} bets the current basket saved bets
   * @param {OutcomeState} outcomes the current basket saved outcomes
   * @return {boolean} check if the stored outcomes and bets are valid with the entities
   */
  static isItemsOutcomesAndBetsValid = (
    items: Array<BetBuilderItem>,
    bets: { [key: number]: BetState },
    outcomes: { [key: number]: OutcomeState },
  ) => BetBuilder.checkItemsOutcomesAndBetsState(items, bets, outcomes);

  static aggregateRelatedBetsAndOutcomes = (
    items: Array<BetBuilderItem>,
    bets: { [key: number]: BetState },
    outcomes: { [key: number]: OutcomeState },
  ) =>
    reduce(
      items,
      (result: Array<{ bet: BetState; outcome: OutcomeState }>, item) => {
        const { betId, outcomeId, outcomeIds } = item;
        const bet = bets[betId];

        if (bet) {
          if (BetBuilder.isMultichoiceItem(item)) {
            outcomeIds?.forEach((id) => {
              if (outcomes[id]) {
                result.push({
                  bet,
                  outcome: outcomes[id],
                });
              }
            });
          } else {
            if (outcomeId) {
              const outcome = outcomes[outcomeId];

              if (outcome) {
                result.push({
                  bet,
                  outcome,
                });
              }
            }
          }
        }
        return result;
      },
      [],
    );
}

export default BetBuilder;
